import type { AlphabetCardsData } from "@/entities/Activity/types/AlphabetCards";
import type React from "react";
import { useContext, useEffect } from "react";
import { MdOutlineOndemandVideo, MdOutlineSpeakerNotes } from "react-icons/md";
import { Link } from "react-router-dom";
import { Button } from "rsuite";
import { SpeakingProvider } from "../../../../app/context";
import { GameContext } from "../../../../app/context/GameContext";
import type {
	AudioTextualDetails,
	FillInGapsTextDetails,
	FlashCardsDetails,
	InteractiveSentencesDetails,
	ListenForSpecificInfoDetails,
	MatchTwoItemsDetails,
	MatchWordsWithPicturesDetails,
	NotesDetails,
	QaAiDetails,
	QuizDetails,
	SnakeGameDetails,
	SvgImageDetails,
	TranslateSentencesDetails,
	UnscrambleSentencesByListeningDetails,
	UnscrambleSentencesDetails,
	VideoLinksActivityDetails,
} from "../../../../entities/Activity/types";
import type { ActivityType } from "../../../../entities/Activity/types/activityType";
import { Instruction } from "../../../../shared/components/Instruction/Instruction";
import { useIsMobile } from "../../../../shared/hooks/useIsMobile";
import { AlphabetCards } from "../../AlphabetCards/AlphabetCards";
import { AlphabetPractice } from "../../AlphabetPractice/AlphabetPractice";
import AudioTextualActivity from "../../AudioTextualActivity/AudioTextualActivity";
import FillInGapsText from "../../FillInGapsText/FillInGapsText";
import FlashCard from "../../Flashcard/FlashCard";
import { InteractiveSentence } from "../../InteractiveSentence/InteractiveSentence";
import ListenForSpecifiInfoActivity from "../../ListenForSpecificInfoActivity/ListenForSpecificInfoActivity";
import MatchWordsActivity from "../../MatchWordsActivity/MatchWordsActivity";
import { MatchWordsActivityMobile } from "../../MatchWordsActivityMobile/MatchWordsActivityMobile";
import MatchWordsWithPicturesActivity from "../../MatchWordsWithPicturesActivity/MatchWordsWithPicturesActivity";
import { MatchWordsWithPicturesMobile } from "../../MatchWordsWithPicturesMobile/MatchWordsWithPicturesMobile";
import Notes from "../../Notes/Notes";
import { QaWithAI } from "../../QaWithAI/QaWithAI";
import QuizComponent from "../../QuizComponent/QuizComponent";
import { SvgAnnotation } from "../../SvgAnnotation/SvgAnnotation";
import TranslateSentencesOrallyActivity from "../../TranslateSentencesOrallyActivity/TranslateSentencesOrallyActivity";
import UnscrambleSentences from "../../UnscrambleSentences/UnscrambleSentences";
import UnscrambleSentencesByListening from "../../UnscrambleSentencesByListening/UnscrambleSentencesByListening";
import VideoLinkActivity from "../../VideoLink/VIdeoLink";
import { ShowCompletedIcon } from "../ShowCompletedIcon/ShowCompletedIcon";
import style from "./ActivityRouter.module.scss";

function generateAcitivites(activity: ActivityType, index: number) {
	const type = activity.type;
	const isMobile = useIsMobile();
	const { setGameModalOpen } = useContext(GameContext);

	switch (type) {
		case "video-link":
			return (
				<div key={index} className={style.customActivity}>
					<div className={style.instructionAndIcon}>
						{(activity?.details as VideoLinksActivityDetails)?.instruction && (
							<Instruction
								instruction={
									(activity?.details as VideoLinksActivityDetails)
										?.instruction || ""
								}
								Icon={<MdOutlineOndemandVideo />}
							/>
						)}
					</div>
					<VideoLinkActivity
						activity={activity.details as VideoLinksActivityDetails}
						activityId={activity._id as string}
					/>
				</div>
			);
		case "audio-textual":
			return (
				<div key={index} className={style.customActivity}>
					<div className={style.instructionAndIcon}>
						{(activity?.details as AudioTextualDetails)?.instruction && (
							<Instruction
								instruction={
									(activity?.details as AudioTextualDetails)?.instruction
								}
								Icon={<MdOutlineOndemandVideo />}
							/>
						)}
					</div>
					<AudioTextualActivity
						activity={activity.details as AudioTextualDetails}
					/>
				</div>
			);
		case "notes":
			return (
				<div key={index} className={style.customActivity}>
					<div className={style.instructionAndIcon}>
						{(activity?.details as NotesDetails)?.instruction && (
							<Instruction
								instruction={(activity?.details as NotesDetails)?.instruction}
								Icon={<MdOutlineSpeakerNotes />}
							/>
						)}
					</div>
					<Notes activity={activity.details as NotesDetails} />
				</div>
			);
		case "translate-sentences-orally":
			return (
				<div key={index} className={style.customActivity}>
					<div className={style.instructionAndIcon}>
						<Instruction
							instruction={
								(activity?.details as TranslateSentencesDetails)?.instruction
							}
						/>
						<ShowCompletedIcon id={activity._id || ""} />
					</div>
					<TranslateSentencesOrallyActivity
						activityId={activity._id || ""}
						data={
							(activity.details as TranslateSentencesDetails).translateSentences
						}
					/>
				</div>
			);

		case "quizes":
			return (
				<div key={index} className={style.customActivity}>
					<div className={style.instructionAndIcon}>
						<Instruction
							instruction={(activity?.details as QuizDetails)?.instruction}
						/>
						<ShowCompletedIcon id={activity._id || ""} />
					</div>
					<QuizComponent
						questions={(activity?.details as QuizDetails).questions}
						activityId={activity._id || ""}
					/>
				</div>
			);
		case "flash-cards":
			return (
				<div key={index} className={style.customActivity}>
					<div className={style.instructionAndIcon}>
						<Instruction
							instruction={
								(activity?.details as FlashCardsDetails)?.instruction
							}
						/>
					</div>
					<FlashCard
						activity={(activity.details as FlashCardsDetails).flashCardsData}
					/>
				</div>
			);
		case "fill-in-gaps": {
			return (
				<div key={index} className={style.customActivity}>
					<div className={style.instructionAndIcon}>
						<Instruction
							instruction={
								(activity?.details as FillInGapsTextDetails)?.instruction
							}
						/>
						<ShowCompletedIcon id={activity._id || ""} />
					</div>
					{(activity?.details as FillInGapsTextDetails)?.fillInGapsText?.map(
						(text: string) => (
							<div className={style.fillInGaps} key={text}>
								<FillInGapsText data={text} activityId={activity._id || ""} />
							</div>
						),
					)}
				</div>
			);
		}

		case "match-two-items":
			if (isMobile) {
				return (
					<div key={index} className={style.customActivity}>
						<div className={style.instructionAndIcon}>
							<Instruction
								instruction={
									(activity?.details as MatchTwoItemsDetails)?.instruction
								}
							/>
							<ShowCompletedIcon id={activity._id || ""} />
						</div>
						<MatchWordsActivityMobile
							activity={
								(activity.details as MatchTwoItemsDetails)
									.matchWordAndDefinition
							}
							activityId={activity._id || ""}
						/>
					</div>
				);
			}

			if (!isMobile) {
				return (
					<div key={index} className={style.customActivity}>
						<div className={style.instructionAndIcon}>
							<Instruction
								instruction={
									(activity?.details as MatchTwoItemsDetails)?.instruction
								}
							/>
							<ShowCompletedIcon id={activity._id || ""} />
						</div>
						<SpeakingProvider>
							<MatchWordsActivity
								data={
									(activity.details as MatchTwoItemsDetails)
										.matchWordAndDefinition
								}
								activityId={activity._id || ""}
							/>
						</SpeakingProvider>
					</div>
				);
			}

			break;
		case "unscramble-sentences":
			return (
				<div key={index} className={style.customActivity}>
					<div className={style.instructionAndIcon}>
						<Instruction
							instruction={
								(activity?.details as UnscrambleSentencesDetails)?.instruction
							}
						/>
						<ShowCompletedIcon id={activity._id || ""} />
					</div>
					<UnscrambleSentences
						data={
							(activity.details as UnscrambleSentencesDetails)
								.unscrambleSentences
						}
						activityId={activity._id || ""}
					/>
				</div>
			);
		case "unscramble-sentences-by-listening":
			return (
				<div key={index} className={style.customActivity}>
					<div className={style.instructionAndIcon}>
						<Instruction
							instruction={
								(activity?.details as UnscrambleSentencesByListeningDetails)
									?.instruction
							}
						/>
						<ShowCompletedIcon id={activity._id || ""} />
					</div>
					<UnscrambleSentencesByListening
						activityId={activity._id || ""}
						data={
							(activity.details as UnscrambleSentencesByListeningDetails)
								.unscrambleSentencesByListening
						}
					/>
				</div>
			);
		case "match-pictures-with-words":
			if (!isMobile) {
				return (
					<div key={index} className={style.customActivity}>
						<div className={style.instructionAndIcon}>
							<Instruction
								instruction={
									(activity?.details as MatchWordsWithPicturesDetails)
										?.instruction
								}
							/>
							<ShowCompletedIcon id={activity._id || ""} />
						</div>
						<MatchWordsWithPicturesActivity
							activityId={activity._id || ""}
							activity={
								(activity.details as MatchWordsWithPicturesDetails)
									.matchPicturesWithWords
							}
						/>
					</div>
				);
			}

			if (isMobile) {
				return (
					<div key={index} className={style.customActivity}>
						<div className={style.instructionAndIcon}>
							<Instruction
								instruction={
									(activity?.details as MatchWordsWithPicturesDetails)
										?.instruction
								}
							/>
							<ShowCompletedIcon id={activity._id || ""} />
						</div>
						<MatchWordsWithPicturesMobile
							activity={
								(activity.details as MatchWordsWithPicturesDetails)
									.matchPicturesWithWords
							}
							activityId={activity._id || ""}
						/>
					</div>
				);
			}
			break;
		case "listen-for-specific-info":
			return (
				<div key={index} className={style.customActivity}>
					<div className={style.instructionAndIcon}>
						<Instruction
							instruction={
								(activity?.details as ListenForSpecificInfoDetails)?.instruction
							}
						/>
						<ShowCompletedIcon id={activity._id || ""} />
					</div>
					{(
						activity?.details as ListenForSpecificInfoDetails
					)?.listenForSpecificInfo?.map(
						(item: { text: string; audio: string }) => (
							<div key={item.text}>
								<ListenForSpecifiInfoActivity
									activityId={activity._id || ""}
									data={item}
								/>
							</div>
						),
					)}
				</div>
			);
		case "svg-image-annotation":
			return (
				<div key={index} className={style.customActivity}>
					<div className={style.instructionAndIcon}>
						{(activity?.details as SvgImageDetails)?.instruction && (
							<Instruction
								instruction={
									(activity?.details as SvgImageDetails)?.instruction
								}
							/>
						)}
						<ShowCompletedIcon id={activity._id || ""} />
					</div>
					<SvgAnnotation svgPath={(activity.details as SvgImageDetails).svg} />
				</div>
			);

		case "alphabet-cards":
			return (
				<div key={index} className={style.customActivity}>
					<div className={style.instructionAndIcon}>
						{(activity?.details as SvgImageDetails)?.instruction && (
							<Instruction
								instruction={
									(activity?.details as AlphabetCardsData)?.instruction
								}
							/>
						)}
					</div>
					<AlphabetCards details={activity.details as AlphabetCardsData} />
					<div className={style.instructionAndIcon}>
						<ShowCompletedIcon id={activity._id || ""} />
					</div>
					<AlphabetPractice
						data={(activity.details as AlphabetCardsData).data}
						activityId={activity._id || ""}
					/>
				</div>
			);

		case "choose-one-of-multiple-in-sentenes":
			return (
				<div key={index} className={style.customActivity}>
					<div className={style.instructionAndIcon}>
						{(activity?.details as InteractiveSentencesDetails)
							?.instruction && (
							<Instruction
								instruction={
									(activity?.details as SvgImageDetails)?.instruction
								}
							/>
						)}
						<ShowCompletedIcon id={activity._id || ""} />
					</div>
					<InteractiveSentence
						activityId={activity._id || ""}
						audio={
							(activity?.details as InteractiveSentencesDetails)?.audio || ""
						}
						sentences={
							(activity?.details as InteractiveSentencesDetails)?.sentences
						}
					/>
				</div>
			);
		case "qa-ai":
			return (
				<div key={index} className={style.customActivity}>
					<div className={style.instructionAndIcon}>
						{(activity?.details as QaAiDetails)?.instruction && (
							<Instruction
								instruction={(activity?.details as QaAiDetails)?.instruction}
							/>
						)}
						<ShowCompletedIcon id={activity._id || ""} />
					</div>
					<QaWithAI
						activityId={activity._id || ""}
						data={(activity.details as QaAiDetails).data}
					/>
				</div>
			);
		case "snake-game":
			return (
				<div key={index} className={style.customActivity}>
					<div className={style.instructionAndIcon}>
						{(activity?.details as SnakeGameDetails)?.instruction && (
							<Instruction
								instruction={
									(activity?.details as SnakeGameDetails)?.instruction
								}
							/>
						)}
						<ShowCompletedIcon id={activity._id || ""} />
					</div>
					<Link
						onClick={() => setGameModalOpen(true)}
						to={`/games/snake-game/${activity._id}`}
					>
						<Button appearance="ghost">Play Snake Game</Button>
					</Link>
				</div>
			);
		case "memory-card-game":
			return (
				<div key={index} className={style.customActivity}>
					<div className={style.instructionAndIcon}>
						{(activity?.details as SnakeGameDetails)?.instruction && (
							<Instruction
								instruction={
									(activity?.details as SnakeGameDetails)?.instruction
								}
							/>
						)}
						<ShowCompletedIcon id={activity._id || ""} />
					</div>
					<Link
						onClick={() => setGameModalOpen(true)}
						to={`/games/memory-card-game/${activity._id}`}
					>
						<Button appearance="ghost">Play Memory Card Game</Button>
					</Link>
				</div>
			);

		default:
			return null;
	}
}

type Props = {
	activity: { details: { customActivities: ActivityType[] } };
};

export const ActivityRouter: React.FC<Props> = ({ activity }) => {
	return (
		<div className={style.customActivityContainer}>
			{activity?.details?.customActivities?.map(
				(activity: ActivityType, index: number) => {
					return (
						<div key={activity._id} className={style.customActivityWrapper}>
							{generateAcitivites(activity, index)}
						</div>
					);
				},
			)}
			<div className={style.customActivityBottom} />
		</div>
	);
};
